import React from 'react';
import {
    Card,
    CardContent,
    Box,
    Typography,
    Button,
    Divider
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import CheckIcon from '@mui/icons-material/Check'; // Checkmark for going
import QuestionMarkIcon from '@mui/icons-material/QuestionMark'; // ? for undecided

// Define props interface
interface EventCardProps {
    eventTitle: string;
    eventDescription: string;
    eventLocation: string;
    eventLink: string;
    eventDate: Date | string;
    width?: string | number; // Optional width prop for flexibility
}

const EventCard: React.FC<EventCardProps> = ({
                                                 eventTitle,
                                                 eventDescription,
                                                 eventDate,
                                                 width = 'auto' // Default to auto width
                                             }) => {
    // Convert eventDate to Date object if it's a string
    const currentDate = eventDate instanceof Date ? eventDate : new Date(eventDate);
    const today = new Date();

    const [attendance, setAttendance] = React.useState<'going' | 'undecided' | 'notGoing'>('undecided');

    const getButtonColor = () => {
        switch (attendance) {
            case 'going':
                return '#4CAF50'; // Green
            case 'undecided':
                return '#B0BEC5'; // Gray
            case 'notGoing':
                return '#FFCDD2'; // Light red
            default:
                return '#B0BEC5'; // Default to gray
        }
    };

    const getButtonIcon = () => {
        switch (attendance) {
            case 'going':
                return <CheckIcon fontSize="small" sx={{ fontSize: '5px' }} />;
            case 'undecided':
                return <QuestionMarkIcon fontSize="small" sx={{ fontSize: '5px' }} />;
            case 'notGoing':
                return <ClearIcon fontSize="small" sx={{ fontSize: '5px' }} />;
            default:
                return <QuestionMarkIcon fontSize="small" sx={{ fontSize: '5px' }} />;
        }
    };
    // Handle button click to cycle through attendance states
    const handleAttendanceClick = () => {
        setAttendance(prev => {
            if (prev === 'undecided') return 'going';
            if (prev === 'going') return 'notGoing';
            return 'undecided';
        });
    };

    // Check if event date is today
    const isToday = today.toDateString() === currentDate.toDateString();

    // Format date
    const formattedDate = currentDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        weekday: 'short',
    });

    const dateParts = formattedDate.split(', ');
    const dayOfWeek = dateParts[0]; // e.g., "Wednesday"
    const [month, day] = dateParts[1].split(' ');
    const year = dateParts[2];

    return (
        <Card sx={{
            width, // Flexible width based on prop
            display: 'flex',
            flexDirection: 'row',
            m: 1
        }}>
            {/* Left side - Date box */}
            <Box
                sx={{
                    width: 125,
                    bgcolor: 'grey.100',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 1,
                    flexShrink: 0 // Prevents date box from shrinking
                }}
            >
                {isToday && (
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{mb: 1}}
                    >
                        Today
                    </Typography>
                )}
                <Typography variant="h5">
                    {dayOfWeek}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                    {month} {day}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {year}
                </Typography>
            </Box>

            {/* Right side - Event details */}
            <Box sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0 // Prevents overflow
            }}>
                <CardContent sx={{flex: 1}}>
                    <Typography variant="h6" component="div" noWrap>
                        {eventTitle}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {eventDescription}
                    </Typography>
                </CardContent>

                <Divider/>

                <Box sx={{p: 1, display: 'flex', justifyContent: 'flex-end'}}>
                    <Button
                        variant="contained"
                        size="small"
                        startIcon={getButtonIcon()}
                        onClick={handleAttendanceClick}
                        sx={{
                            minWidth: 'auto',
                            px: 1.5,
                            py: 0.5,
                            bgcolor: getButtonColor(),
                            backgroundImage: 'none', // Explicitly remove any background image
                            boxShadow: 0,
                            color: attendance === 'notGoing' ? '#000' : '#fff', // Black text for light red, white for others
                            '&:hover': {
                                bgcolor: getButtonColor(),
                                color: attendance === 'notGoing' ? '#000' : '#fff'
                            }
                        }}
                    >
                        Eleea
                    </Button>
                </Box>
            </Box>
        </Card>
    );
};

export default EventCard;